import React, { useRef, useState } from 'react';
import { updateLayoutImage, uploadImage } from '../../../../shared/api/imageProjectApi';
import { setSelectedImage, setAllImagesForLayouts } from '../../../../store/slices/imageSlice';
import { addImagesToLayout } from '../../../../shared/api/imageLayoutApi';
import { useAppDispatch } from '../../../../store/store';
import { useSelector } from 'react-redux';
import { selectImageLayout } from '../../../../store/selectors/imageSelectors';
import s from './UploadImage.module.css';
import {
    attachVideoToProject,
    requestVideoPresign,
    uploadVideoToStorage,
} from '../../../../shared/api/videoProjectApi';
import { toast } from 'react-toastify';
import { selectCurrentProject } from '../../../../store/selectors/projectSelectors';
import { extractVideoFrameAtTime } from '../../../../shared/helpers/extractVideoFrames';

const UploadImage = ({ saveImage }: { saveImage: (file: File) => void }) => {
    const dispatch = useAppDispatch();
    const activeLayout = useSelector(selectImageLayout);
    const currentProject = useSelector(selectCurrentProject);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);

    const handleButtonClick = () => {
        inputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            if (file.type.startsWith('image/')) {
                const result: any = await dispatch(uploadImage({ file }));
                if (
                    'payload' in result &&
                    result.payload &&
                    typeof result.payload === 'object' &&
                    'id' in result.payload
                ) {
                    const imageId = result.payload.id;
                    await dispatch(
                        addImagesToLayout({
                            images: [{ id: imageId, order: 1 }],
                            layoutId: activeLayout?.id ?? '',
                        }),
                    );
                    dispatch(setAllImagesForLayouts(result.payload));
                    dispatch(
                        setSelectedImage({
                            image: result.payload,
                            layoutId: activeLayout?.id ?? '',
                        }),
                    );
                }
            } else if (file.type.startsWith('video/')) {
                const url = URL.createObjectURL(file);
                const videoEl = document.createElement('video');

                const videoData = await new Promise<{
                    duration: number;
                    width: number;
                    height: number;
                }>((resolve, reject) => {
                    videoEl.preload = 'metadata';
                    videoEl.onloadedmetadata = () => {
                        URL.revokeObjectURL(url);
                        resolve({
                            duration: videoEl.duration,
                            width: videoEl.videoWidth,
                            height: videoEl.videoHeight,
                        });
                    };
                    videoEl.onerror = () => {
                        URL.revokeObjectURL(url);
                        reject(new Error('Invalid video file'));
                    };
                    videoEl.src = url;
                });

                if (videoData.duration > 180) {
                    toast.error('Video must be 3 minutes or less.');
                    return;
                }

                const aspectRatio = videoData.width / videoData.height;
                const targetRatio = 9 / 16;
                const tolerance = 0.1;
                if (Math.abs(aspectRatio - targetRatio) > tolerance) {
                    toast.error('Video must have 9:16 aspect ratio (vertical format).');
                    return;
                }

                try {
                    const frameBlob = await extractVideoFrameAtTime(
                        file,
                        videoData.duration / 2,
                        0.8,
                    );
                    const frameFile = new File([frameBlob], 'video_frame.jpg', {
                        type: 'image/jpeg',
                    });

                    const result: any = await dispatch(uploadImage({ file: frameFile }));
                    if (
                        'payload' in result &&
                        result.payload &&
                        typeof result.payload === 'object' &&
                        'id' in result.payload
                    ) {
                        if (activeLayout?.id) {
                            await dispatch(
                                updateLayoutImage({ layoutId: activeLayout.id, file: frameFile }),
                            );
                        }
                    }
                } catch (frameError) {
                    toast.error('Failed to extract frame from video');
                }

                const presignAction: any = await dispatch(requestVideoPresign({ file }));
                if (!requestVideoPresign.fulfilled.match(presignAction)) {
                    toast.error((presignAction.payload as string) || 'Presign request failed');
                    return;
                }

                const presignData = presignAction.payload;
                const videoId = presignData.id;

                const uploadAction: any = await dispatch(
                    uploadVideoToStorage({
                        presign: {
                            method: 'PUT',
                            url: presignData.uploadUrl,
                            fileUrl: presignData.url,
                        },
                        file,
                    }),
                );

                if (!uploadVideoToStorage.fulfilled.match(uploadAction)) {
                    toast.error((uploadAction.payload as string) || 'Video upload failed');
                    return;
                }

                const projectId = currentProject?.id || '<<PROJECT_ID>>';
                const attachAction: any = await dispatch(
                    attachVideoToProject({ projectId, videoId }),
                );

                if (attachVideoToProject.fulfilled.match(attachAction)) {
                    console.log('Video uploaded and linked to project.');
                    // toast.success('Video uploaded and linked to project.');
                } else {
                    toast.error(
                        (attachAction.payload as string) ||
                            'Video uploaded, but attaching to project failed.',
                    );
                }
            } else {
                console.warn('Unsupported file type:', file.type);
            }
        } finally {
            e.target.value = '';
        }
    };

    return (
        <div className={s.upload_image_container}>
            <button className={s.upload_image_button} onClick={handleButtonClick}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="60"
                    height="60"
                    viewBox="0 0 60 60"
                    fill="none"
                >
                    <path
                        d="M43.75 57.5C34.79 57.5 27.5 50.21 27.5 41.25C27.5 32.29 34.79 25 43.75 25C52.71 25 60 32.29 60 41.25C60 50.21 52.71 57.5 43.75 57.5ZM43.75 27.5C36.17 27.5 30 33.6675 30 41.25C30 48.8325 36.17 55 43.75 55C51.33 55 57.5 48.8325 57.5 41.25C57.5 33.6675 51.33 27.5 43.75 27.5Z"
                        fill="#2E2E2E"
                    />
                    <path
                        d="M51.2501 42.4996C50.9301 42.4996 50.6101 42.3771 50.3651 42.1346L43.7501 35.5171L37.1351 42.1322C36.6476 42.6196 35.8551 42.6196 35.3676 42.1322C34.8801 41.6447 34.8801 40.8521 35.3676 40.3646L42.8676 32.8646C43.3551 32.3771 44.1476 32.3771 44.6351 32.8646L52.1351 40.3646C52.6226 40.8521 52.6226 41.6447 52.1351 42.1322C51.8901 42.3772 51.5701 42.4996 51.2501 42.4996Z"
                        fill="#2E2E2E"
                    />
                    <path
                        d="M43.75 50C43.06 50 42.5 49.44 42.5 48.75V34.375C42.5 33.685 43.06 33.125 43.75 33.125C44.44 33.125 45 33.685 45 34.375V48.75C45 49.44 44.44 50 43.75 50Z"
                        fill="#2E2E2E"
                    />
                    <path
                        d="M21.25 37.5H11.25C5.0475 37.5 0 32.4525 0 26.25C0 20.1075 4.9475 15.1 11.0675 15C12.7925 7.69 19.2675 2.5 26.875 2.5C33.46 2.5 39.36 6.49 41.8675 12.53C42.075 12.51 42.285 12.5 42.5 12.5C47.5625 12.5 52.1 15.53 54.055 20.2175C54.3175 20.855 54.0175 21.585 53.38 21.8525C52.7525 22.12 52.01 21.82 51.745 21.18C50.18 17.425 46.55 15 42.5 15C42.0675 15 41.6575 15.07 41.245 15.135C40.6325 15.2375 40.065 14.88 39.8675 14.305C37.96 8.74 32.7375 5 26.875 5C20.115 5 14.41 9.8475 13.3075 16.5275C13.2025 17.1725 12.6 17.64 11.965 17.57L11.645 17.535C11.5125 17.5175 11.3825 17.5 11.25 17.5C6.425 17.5 2.5 21.425 2.5 26.25C2.5 31.075 6.425 35 11.25 35H21.25C21.94 35 22.5 35.56 22.5 36.25C22.5 36.94 21.94 37.5 21.25 37.5Z"
                        fill="#2E2E2E"
                    />
                </svg>
            </button>
            <input
                ref={inputRef}
                type="file"
                accept="image/*,video/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
            <p className={s.title}>Upload photo or video</p>
            <p className={s.subtitle}>
                Max video length 3 minutes, 9:16 aspect ratio required. Image size not above 8 MB.
                (Supported formats: PNG, JPEG, HEIC & MP4 for video)
            </p>
        </div>
    );
};

export default UploadImage;
